export default {
  index: {
    title: 'Guider om tidevann og strøm',
    description:
      'Lær hvordan tidevann og tidevannsstrømmer fungerer: tidevannskoeffisienter, flo- og fjærestrømmer, tidevannsreferanser, og hvordan tidevannsvarsler beregnes.',
    subtitle: 'Korte, lettfattelige forklaringer på vitenskapen bak varsler for tidevann og strøm.',
  },
  back: 'Tilbake til alle guider',
  guides: {
    'tidal-coefficients': {
      title: 'Tidevannskoeffisienter forklart',
      description:
        'Hva tidevannskoeffisienter betyr: skalaen fra 20 til 120, hvordan springflo og nippflo henger sammen med Månen, og hvorfor en høyere koeffisient gir større tidevannsforskjell.',
      blurb:
        'Skalaen fra 20 til 120 som oppsummerer hvor stort et tidevann blir, og hvordan Månen driver det.',
      body: [
        {
          p: 'En tidevannskoeffisient er ett enkelt tall som oppsummerer hvor stort tidevannet blir på en gitt dag. Den brukes mye i Frankrike og andre land som følger den franske hydrografiske tradisjonen. Koeffisienten er dimensjonsløs og går normalt fra omtrent 20 til 120: jo større tallet er, desto større er forskjellen mellom høyvann og lavvann, og desto sterkere er de tilhørende tidevannsstrømmene.',
        },
        { h2: 'Skalaen fra 20 til 120' },
        {
          p: 'Koeffisienten sammenligner dagens tidevannsforskjell med en langsiktig referanse. Flere punkter på skalaen har en fast betydning:',
        },
        {
          list: [
            { term: '20', text: 'det minste tidevannet, nær den minste astronomiske forskjellen.' },
            { term: '45', text: 'en gjennomsnittlig nippflo, når forskjellen er liten.' },
            {
              term: '70',
              text: 'et middeltidevann, omtrent midtveis mellom nippflo og springflo.',
            },
            { term: '95', text: 'en gjennomsnittlig springflo, når forskjellen er stor.' },
            {
              term: '120',
              text: 'det største tidevannet, nær den største astronomiske forskjellen.',
            },
          ],
        },
        { h2: 'Hvorfor den endrer seg: Månen' },
        {
          p: 'Koeffisientene stiger og faller i den omtrent to uker lange syklusen mellom springflo og nippflo. Springflo, med høye koeffisienter, inntreffer en dag eller to etter hver ny- og fullmåne, når Solen og Månen står på linje og tidevannskreftene deres legges sammen. Nippflo, med lave koeffisienter, inntreffer nær første og siste kvarter, når Solen og Månen står vinkelrett på hverandre og delvis opphever hverandre.',
        },
        {
          p: 'Månens avstand har også betydning. Når en springflo faller sammen med perigeum (Månens nærmeste punkt til Jorden), når koeffisienten sine høyeste verdier. De aller største tidevannene har en tendens til å samle seg nær vår- og høstjevndøgn.',
        },
        { h2: 'Hva en koeffisient ikke forteller deg' },
        {
          p: 'Koeffisienten beskriver bare den astronomiske forskjellen. Den tar ikke med værets virkning: vind og lufttrykk kan heve eller senke den faktiske vannstanden uavhengig av koeffisienten. Den er dessuten en egenskap ved tidevannet som helhet for den dagen, ikke en høyde i meter eller fot på et enkelt sted.',
        },
      ],
    },
    'tidal-currents': {
      title: 'Flo, fjære og strømstille: slik fungerer tidevannsstrømmer',
      description:
        'Slik fungerer tidevannsstrømmer: retningen på flo og fjære, strømstille ved vendingen, strømfart i knop og strømretning, og hvorfor strømstille ikke alltid faller sammen med høyvann og lavvann.',
      blurb:
        'Flo, fjære og strømstille, og hvorfor den sterkeste strømmen ikke alltid er ved høyvann.',
      body: [
        {
          p: 'Tidevannsstrømmer er den vannrette bevegelsen av vann som følger tidevannets loddrette stigning og fall. Der tidevannshøyden forteller hvor dypt vannet er, forteller strømmen hvilken vei det renner og hvor fort. Strømmer betyr mest i løp, innløp og elvemunninger, der de kan gå med flere knop.',
        },
        { h2: 'Flo, fjære og strømstille' },
        {
          list: [
            {
              term: 'Flostrøm',
              text: 'vann som renner inn mot land eller opp i en elvemunning, vanligvis mens tidevannet stiger.',
            },
            {
              term: 'Fjærestrøm',
              text: 'vann som renner ut mot havet eller ned gjennom en elvemunning, vanligvis mens tidevannet faller.',
            },
            {
              term: 'Strømstille',
              text: 'den korte pausen der strømmen er nær null mens den vender mellom flo og fjære.',
            },
          ],
        },
        {
          p: 'Strømfarten måles i knop (nautiske mil i timen). Retningen, kalt strømretningen, angis som en kompasspeiling: den retningen vannet renner mot.',
        },
        { h2: 'Strømstille er ikke alltid ved høyvann' },
        {
          p: 'En vanlig antakelse er at strømmen stopper ved høyvann og lavvann. Det stemmer bare noen steder. Tidspunktet avhenger av hvordan tidevannsbølgen oppfører seg lokalt. Der tidevannet oppfører seg som en fremadskridende bølge, som i et langt åpent løp, inntreffer den sterkeste floen og fjæren nær høyvann og lavvann, og strømstille faller nær midtpunktet. Der det oppfører seg som en stående bølge, som innerst i en lukket bukt, er det omvendt: den raskeste strømmen er nær midttidevann, og strømstille inntreffer nær høyvann og lavvann. Lokale strømtabeller er den pålitelige veiledningen.',
        },
        { h2: 'Vendende og roterende strømmer' },
        {
          p: 'I trange løp er strømmen vanligvis vendende: den går én vei under flo, snur så og går tilbake under fjære. I åpent kystvann er strømmen ofte roterende i stedet og skifter retning kontinuerlig hele kompasset rundt gjennom en tidevannssyklus, i stedet for å vende langs en enkelt linje.',
        },
      ],
    },
    'tidal-datums': {
      title: 'Tidevannsreferanser og tidevannshøyder',
      description:
        'Hva en tidevannshøyde måles fra: sjøkartnull, laveste astronomiske tidevann (LAT), midlere lavere lavvann (MLLW), og hvorfor referansene er forskjellige fra land til land.',
      blurb: 'Sjøkartnull, LAT, MLLW og hva tallet i en tidevannshøyde egentlig betyr.',
      body: [
        {
          p: 'Enhver tidevannshøyde er en måling over et referansenivå som kalles en referanse. Når en tidevannstabell sier at vannet skal nå 1.8 meter, betyr det 1.8 meter over den lokale tidevannsreferansen: ikke over havbunnen og ikke over havoverflaten i dagligdags forstand. Å kjenne referansen er det som gjør en høyde meningsfull.',
        },
        { h2: 'Hva en tidevannsreferanse er' },
        {
          p: 'En tidevannsreferanse er en referansehøyde som defineres av et bestemt stadium av tidevannet, midlet over en lang periode: vanligvis omtrent 19 år, noe som jevner ut den 18.6 år lange syklusen i Månens bane. Fordi den er et gjennomsnitt av virkelige observasjoner, er en referanse spesifikk for et sted og for myndigheten som beregnet den.',
        },
        { h2: 'Sjøkartnull og vanlige referanser' },
        {
          p: 'Sjøkart bruker en lavvannsreferanse, slik at den faktiske dybden nesten alltid er minst den oppgitte dybden. Dette sjøkartnullet er nullpunktet som både de oppgitte dybdene og tidevannshøydene regnes fra. Ulike land bruker ulike referanser:',
        },
        {
          list: [
            {
              term: 'LAT (laveste astronomiske tidevann)',
              text: 'det laveste nivået som kan forutses under gjennomsnittlig vær; brukes av Storbritannia og mange andre nasjoner.',
            },
            {
              term: 'MLLW (midlere lavere lavvann)',
              text: 'gjennomsnittet av det laveste av hver dags to lavvann; standardreferansen på sjøkart i USA.',
            },
            {
              term: 'MSL (midlere havnivå)',
              text: 'den langsiktige gjennomsnittlige vannstanden, brukt hovedsakelig som referanse for landhøyder heller enn for sjøkart.',
            },
          ],
        },
        {
          p: 'Høyvannsreferanser som midlere høyvann (MHW) og midlere høyere høyvann (MHHW) brukes til det motsatte formålet, for eksempel til å fastsette en strandlinje eller en fri høyde under en bro.',
        },
        { h2: 'Hvorfor høyder ikke er direkte sammenlignbare' },
        {
          p: 'Fordi referansene er forskjellige, kan en høyde ved én stasjon ikke sammenlignes direkte med en høyde ved en annen med mindre begge bruker samme referanse. Et lavvann på 0.5 meter målt fra LAT er ikke den samme vannstanden som et lavvann på 0.5 meter målt fra MLLW. Dette er også grunnen til at det samme fysiske tidevannet kan beskrives med forskjellige tall i tabellene til ulike land.',
        },
      ],
    },
    'how-tide-predictions-work': {
      title: 'Slik beregnes tidevannsvarsler',
      description:
        'Hvordan tidevann varsles: tyngdepåvirkning fra Månen og Solen, harmoniske komponenter som M2 og S2, og hvorfor observerte vannstander avviker fra det astronomiske varselet.',
      blurb:
        'Harmonisk analyse, komponentene bak tidevannet, og hvorfor været får virkeligheten til å avvike.',
      body: [
        {
          p: 'Tidevann kan varsles år i forveien fordi det drives av bevegelsene til Månen og Solen, som er kjent med stor presisjon. Tidevannet på et sted er havets respons på denne tyngdepåvirkningen, formet av den lokale kystlinjen og dybden. Metoden som gjør astronomi om til en tidevannstabell, kalles harmonisk analyse.',
        },
        { h2: 'Tidevannet som en sum av komponenter' },
        {
          p: 'Harmonisk analyse betrakter tidevannet som summen av mange enkle bølger, som hver kalles en komponent. Hver komponent har en frekvens som er fastsatt av astronomien (av hvordan Jorden, Månen og Solen beveger seg i forhold til hverandre) mens amplituden og tidspunktet finnes ved å tilpasse komponentene til en periode med virkelige vannstandsobservasjoner på stedet. De viktigste komponentene omfatter:',
        },
        {
          list: [
            {
              term: 'M2',
              text: 'den viktigste halvdaglige månekomponenten, periode 12.42 timer; vanligvis den største.',
            },
            { term: 'S2', text: 'den viktigste halvdaglige solkomponenten, periode 12.00 timer.' },
            { term: 'N2', text: 'en månekomponent som stammer fra Månens elliptiske bane.' },
            {
              term: 'K1 og O1',
              text: 'de viktigste daglige komponentene, som mange steder gjør det ene daglige høyvannet større enn det andre.',
            },
          ],
        },
        {
          p: 'Den velkjente syklusen mellom springflo og nippflo oppstår naturlig av dette: M2 og S2 har litt ulike perioder, så de kommer i og ut av takt: hver 14.8 dag forsterker de hverandre til springflo og opphever delvis hverandre til nippflo.',
        },
        { h2: 'Varsling framover' },
        {
          p: 'Når amplituden og fasen til hver komponent er kjent for et sted, er det å varsle tidevannet bare et spørsmål om å beregne summen på et hvilket som helst fremtidig tidspunkt. De astronomiske frekvensene endrer seg aldri, så beregningen er deterministisk og kan gå tiår fremover.',
        },
        { h2: 'Hvorfor det virkelige tidevannet avviker' },
        {
          p: 'Et varsel er bare det astronomiske tidevannet. Det tar ikke med været. Sterk vind, lavt eller høyt lufttrykk, kraftig nedbør og vannføring fra elver kan heve eller senke den faktiske vannstanden: en stormflo kan legge en meter eller mer oppå varselet, mens høyt trykk og fralandsvind kan holde vannet et godt stykke under det. Varsler er derfor en utmerket rettesnor for tidevannets tidspunkt og størrelse, men den observerte vannstanden på selve dagen kan avvike.',
        },
      ],
    },
  },
}
